// src/features/admin/components/settings/document-management/components/font-manager/FontUpload.tsx
import React, { useState, useCallback } from 'react';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { uploadCustomFont } from '../../../../../../wordprocessing/services/FontAPI';

interface FontUploadForm {
  family: string;
  category: 'serif' | 'sans-serif' | 'monospace';
  court_compliant: boolean;
  files: File[];
}

interface UploadStatus {
  status: 'idle' | 'validating' | 'uploading' | 'success' | 'error';
  message?: string;
}

/**
 * Font Upload Component
 * 
 * Allows firm administrators to upload new font files and configure metadata.
 * Validates font files and provides feedback on upload status.
 */
const FontUpload: React.FC = () => {
  const [formData, setFormData] = useState<FontUploadForm>({
    family: '',
    category: 'serif',
    court_compliant: false,
    files: []
  });

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({ status: 'idle' });
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files: File[]) => {
    const validExtensions = ['.ttf', '.otf', '.woff', '.woff2'];
    
    // Validate all files
    const invalidFiles = files.filter(file => {
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      return !validExtensions.includes(fileExtension);
    });
    
    if (invalidFiles.length > 0) {
      setUploadStatus({
        status: 'error',
        message: `Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Please upload TTF, OTF, WOFF, or WOFF2 files only.`
      });
      return;
    }

    // Auto-populate font family from first filename if empty
    // Extract family name: "Carlito-Bold.ttf" -> "Carlito"
    if (!formData.family && files.length > 0) {
      const firstFile = files[0].name;
      const familyName = firstFile
        .replace(/\.(ttf|otf|woff2?)/i, '')
        .split(/[-_]/)[0]
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
      
      setFormData(prev => ({
        ...prev,
        family: familyName,
        files
      }));
    } else {
      setFormData(prev => ({ ...prev, files }));
    }

    setUploadStatus({ status: 'idle' });
  };

  const validateForm = (): boolean => {
    if (!formData.files || formData.files.length === 0) {
      setUploadStatus({ status: 'error', message: 'Please select at least one font file to upload.' });
      return false;
    }
    if (!formData.family.trim()) {
      setUploadStatus({ status: 'error', message: 'Please enter a font family name.' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setUploadStatus({ status: 'validating', message: `Validating ${formData.files.length} font file(s)...` });

      // Prepare form data for upload (multiple files)
      const formDataToSend = new FormData();
      
      // Append all files
      formData.files.forEach(file => {
        formDataToSend.append('files', file);
      });
      
      // Append metadata
      formDataToSend.append('fontFamily', formData.family);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('courtCompliant', formData.court_compliant.toString());

      setUploadStatus({ status: 'uploading', message: `Uploading ${formData.files.length} variant(s) to server...` });

      // Upload fonts to backend
      const uploadedFonts = await uploadCustomFont(formDataToSend);

      console.log('[FontUpload] Fonts uploaded successfully:', uploadedFonts);

      setUploadStatus({ 
        status: 'success', 
        message: `Successfully uploaded ${Array.isArray(uploadedFonts) ? uploadedFonts.length : 1} font variant(s) for "${formData.family}"! They are now available in the registry.`
      });

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          family: '',
          category: 'serif',
          court_compliant: false,
          files: []
        });
        setUploadStatus({ status: 'idle' });
      }, 3000);

    } catch (error: any) {
      console.error('Font upload failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed. Please try again.';
      setUploadStatus({ 
        status: 'error', 
        message: errorMessage
      });
    }
  };

  const handleReset = () => {
    setFormData({
      family: '',
      category: 'serif',
      court_compliant: false,
      files: []
    });
    setUploadStatus({ status: 'idle' });
  };

  return (
    <div className="font-upload">
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Upload New Font
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Add custom fonts to your firm's library. Uploaded fonts will be available in the EdgeDocs toolbar dropdown after processing.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Font File *
              </label>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-lg p-8 transition-colors ${
                  dragActive
                    ? 'border-lex-accent bg-lex-accent-soft'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="text-center">
                  <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    {formData.files.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formData.files.length} file(s) selected
                        </p>
                        <div className="max-h-32 overflow-y-auto text-left bg-gray-50 dark:bg-gray-700 rounded p-2">
                          {formData.files.map((file, idx) => (
                            <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 py-1">
                              • {file.name} ({(file.size / 1024).toFixed(1)} KB)
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={handleReset}
                          className="text-sm text-lex-accent hover:text-lex-accent-hover"
                        >
                          Choose different files
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Drag and drop font files here (multiple files supported), or
                        </p>
                        <label className="mt-2 inline-block">
                          <span className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover cursor-pointer transition-colors">
                            Browse Files
                          </span>
                          <input
                            type="file"
                            accept=".ttf,.otf,.woff,.woff2"
                            onChange={handleFileInput}
                            multiple
                            className="hidden"
                          />
                        </label>
                      </>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Supported formats: TTF, OTF, WOFF, WOFF2. Upload all variants (Regular, Bold, Italic, etc.) at once.
                  </p>
                </div>
              </div>
            </div>

            {/* Font Metadata */}
            <div className="bg-lex-accent-soft border border-lex-accent/30 rounded-lg p-4 mb-4">
              <p className="text-sm text-lex-accent mb-2">
                <strong>Auto-Detection:</strong> Weight and style (Regular, Bold, Italic, etc.) are automatically detected from filenames.
              </p>
              <p className="text-xs text-lex-accent">
                Example: "Carlito-BoldItalic.ttf" will be detected as Bold (700) Italic
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Font Family * (auto-filled from filename)
                </label>
                <input
                  type="text"
                  value={formData.family}
                  onChange={(e) => setFormData(prev => ({ ...prev, family: e.target.value }))}
                  placeholder="e.g., Carlito, Roboto, Lora"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  All variants should share the same family name (e.g., "Carlito" for Carlito-Regular.ttf, Carlito-Bold.ttf, etc.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                >
                  <option value="serif">Serif</option>
                  <option value="sans-serif">Sans-Serif</option>
                  <option value="monospace">Monospace</option>
                </select>
              </div>

              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="court-compliant"
                  checked={formData.court_compliant}
                  onChange={(e) => setFormData(prev => ({ ...prev, court_compliant: e.target.checked }))}
                  className="h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                />
                <label htmlFor="court-compliant" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Mark as Court Compliant
                </label>
              </div>
            </div>

            {/* Status Message */}
            {uploadStatus.status !== 'idle' && (
              <div className={`rounded-lg p-4 ${
                uploadStatus.status === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : uploadStatus.status === 'error'
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  : 'bg-lex-accent-soft border border-lex-accent/30'
              }`}>
                <div className="flex">
                  {uploadStatus.status === 'success' && (
                    <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                  )}
                  {uploadStatus.status === 'error' && (
                    <XCircleIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" />
                  )}
                  {(uploadStatus.status === 'validating' || uploadStatus.status === 'uploading') && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-lex-accent mr-3 flex-shrink-0"></div>
                  )}
                  <p className={`text-sm ${
                    uploadStatus.status === 'success' 
                      ? 'text-green-700 dark:text-green-300'
                      : uploadStatus.status === 'error'
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-lex-accent'
                  }`}>
                    {uploadStatus.message}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={uploadStatus.status === 'validating' || uploadStatus.status === 'uploading'}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={uploadStatus.status === 'validating' || uploadStatus.status === 'uploading' || formData.files.length === 0}
                className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                Upload Font{formData.files.length > 1 ? 's' : ''}
              </button>
            </div>
          </form>
        </div>

        {/* Info Panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-4">
            {/* Upload Guidelines */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <InformationCircleIcon className="h-5 w-5 mr-2 text-lex-accent" />
                Upload Guidelines
              </h5>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Bulk Upload:</strong> Select all font variants at once (Regular, Bold, Italic, etc.)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Auto-Detection:</strong> Weight and style are detected from filenames</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Use TTF or OTF formats for HarfBuzz compatibility</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Maximum: 10 files, 5MB each</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Ensure proper licensing rights (e.g., Google Fonts, SIL Open Font License)</span>
                </li>
              </ul>
            </div>

            {/* Court Compliance Info */}
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 p-4">
              <h5 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                Court Compliance
              </h5>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Mark fonts as "Court Compliant" only if they meet your jurisdiction's requirements for legal document formatting (typically Times New Roman or similar serif fonts at 12-14pt).
              </p>
            </div>

            {/* License Notice */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-500" />
                License Notice
              </h5>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Your firm is responsible for ensuring all uploaded fonts comply with applicable license terms. EdgeLex does not verify font licensing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FontUpload;

